import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Shield, Download, Trash2, Eye, ToggleLeft, ToggleRight, 
  AlertTriangle, CheckCircle, Loader2, FileText, Clock,
  Mail, Bell, BarChart3, Users, X, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GDPRManager = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [consents, setConsents] = useState([]);
  const [deletionStatus, setDeletionStatus] = useState(null);
  const [dataPreview, setDataPreview] = useState(null);
  const [showDataPreview, setShowDataPreview] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadGDPRData();
  }, []);

  const loadGDPRData = async () => {
    const token = localStorage.getItem('token');
    try {
      setLoading(true);
      const [consentsRes, deletionRes] = await Promise.all([
        axios.get(`${API}/gdpr/consents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/gdpr/delete-account/status`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setConsents(consentsRes.data.consents || []);
      setDeletionStatus(deletionRes.data);
    } catch (error) {
      console.error('Error loading GDPR data:', error);
      toast.error('Errore nel caricamento dati privacy');
    } finally {
      setLoading(false);
    }
  };

  const toggleConsent = async (consentType, currentValue) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(
        `${API}/gdpr/consents/${consentType}`,
        { consent_type: consentType, granted: !currentValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Aggiorna lo stato locale
      setConsents(prev => prev.map(c => 
        c.type === consentType ? { ...c, granted: !currentValue } : c
      ));
      
      toast.success(`Consenso ${!currentValue ? 'attivato' : 'revocato'}`);
    } catch (error) {
      console.error('Error updating consent:', error);
      toast.error('Errore nell\'aggiornamento del consenso');
    }
  };

  const previewMyData = async () => {
    const token = localStorage.getItem('token');
    try {
      setProcessing(true);
      const res = await axios.get(`${API}/gdpr/my-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDataPreview(res.data);
      setShowDataPreview(true);
    } catch (error) {
      console.error('Error loading data preview:', error);
      toast.error('Errore nel caricamento anteprima dati');
    } finally {
      setProcessing(false);
    }
  };

  const downloadMyData = async () => {
    const token = localStorage.getItem('token');
    try {
      setProcessing(true);
      const res = await axios.get(`${API}/gdpr/my-data/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Crea link per download
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `primevision_dati_personali_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download completato!');
    } catch (error) {
      console.error('Error downloading data:', error);
      toast.error('Errore nel download dei dati');
    } finally {
      setProcessing(false);
    }
  };

  const requestAccountDeletion = async () => {
    const token = localStorage.getItem('token');
    try {
      setProcessing(true);
      await axios.post(
        `${API}/gdpr/delete-account/request`,
        { reason: deleteReason, confirm_deletion: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Richiesta di cancellazione inviata');
      setShowDeleteConfirm(false);
      loadGDPRData(); // Ricarica per mostrare lo stato
    } catch (error) {
      console.error('Error requesting deletion:', error);
      toast.error(error.response?.data?.detail || 'Errore nella richiesta di cancellazione');
    } finally {
      setProcessing(false);
    }
  };

  const cancelDeletionRequest = async () => {
    const token = localStorage.getItem('token');
    try {
      setProcessing(true);
      await axios.post(
        `${API}/gdpr/delete-account/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Richiesta di cancellazione annullata');
      loadGDPRData();
    } catch (error) {
      console.error('Error cancelling deletion:', error);
      toast.error('Errore nell\'annullamento della richiesta');
    } finally {
      setProcessing(false);
    }
  };

  const getConsentIcon = (type) => {
    switch (type) {
      case 'marketing': return Mail;
      case 'newsletter': return Bell;
      case 'analytics': return BarChart3;
      case 'third_party': return Users;
      default: return Shield;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#2A9FCB]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-[#2A9FCB]/10 flex items-center justify-center">
          <Shield className="w-6 h-6 text-[#2A9FCB]" />
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-[#2A9FCB]" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
            Privacy & GDPR
          </h2>
          <p className="text-sm text-gray-500">Gestisci i tuoi dati e le preferenze di privacy</p>
        </div>
      </div>

      {/* Alert se c'è richiesta cancellazione in corso */}
      {deletionStatus?.has_pending_request && (
        <div className="rounded-xl p-5 bg-amber-50 border border-amber-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-amber-900">Richiesta di cancellazione in corso</h4>
              <p className="text-sm text-amber-700 mt-1">
                I tuoi dati saranno eliminati entro il{' '}
                <strong>{new Date(deletionStatus.scheduled_deletion_date).toLocaleDateString('it-IT')}</strong>
              </p>
              {deletionStatus.can_cancel && (
                <button
                  onClick={cancelDeletionRequest}
                  disabled={processing}
                  className="mt-3 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors disabled:opacity-50"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Annulla Richiesta'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sezione 1: I Miei Dati (Art. 15) */}
      <div className="rounded-xl p-6 bg-[#F9FAFB] border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-[#2A9FCB]" />
          <h3 className="text-lg font-bold text-[#2A9FCB]">I Miei Dati</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Ai sensi dell'Art. 15 del GDPR, hai diritto di accedere a tutti i tuoi dati personali in nostro possesso.
        </p>
        
        <div className="flex flex-wrap gap-3">
          <button
            onClick={previewMyData}
            disabled={processing}
            className="px-4 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
            data-testid="preview-my-data-btn"
          >
            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            Visualizza Anteprima
          </button>
          <button
            onClick={downloadMyData}
            disabled={processing}
            className="px-4 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 bg-[#2A9FCB] text-white hover:bg-[#1C729E] transition-colors disabled:opacity-50"
            data-testid="download-my-data-btn"
          >
            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Scarica i Miei Dati (PDF)
          </button>
        </div>
      </div>

      {/* Sezione 2: Gestione Consensi (Art. 7) */}
      <div className="rounded-xl p-6 bg-[#F9FAFB] border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <ToggleRight className="w-5 h-5 text-[#2A9FCB]" />
          <h3 className="text-lg font-bold text-[#2A9FCB]">Gestione Consensi</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Puoi revocare i consensi opzionali in qualsiasi momento. Il trattamento dei dati sanitari è necessario per usufruire dei servizi.
        </p>

        <div className="space-y-3">
          {consents.map((consent) => {
            const IconComponent = getConsentIcon(consent.type);
            return (
              <div
                key={consent.type}
                className={`p-4 rounded-xl bg-white border ${consent.required ? 'border-gray-200 opacity-75' : 'border-gray-200'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${consent.granted ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <IconComponent className={`w-5 h-5 ${consent.granted ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h4 className="font-medium text-[#1A1A1A]">
                        {consent.name}
                        {consent.required && <span className="ml-2 text-xs text-amber-600">(Necessario)</span>}
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5">{consent.description}</p>
                    </div>
                  </div>
                  
                  {!consent.required ? (
                    <button
                      onClick={() => toggleConsent(consent.type, consent.granted)}
                      className="flex-shrink-0"
                      data-testid={`toggle-consent-${consent.type}`}
                    >
                      {consent.granted ? (
                        <ToggleRight className="w-10 h-10 text-green-500" />
                      ) : (
                        <ToggleLeft className="w-10 h-10 text-gray-300" />
                      )}
                    </button>
                  ) : (
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                  )}
                </div>
                
                {consent.granted_at && (
                  <p className="text-xs text-gray-400 mt-2 pl-13">
                    Consenso dato il {new Date(consent.granted_at).toLocaleDateString('it-IT')}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Sezione 3: Cancellazione Account (Art. 17) */}
      {!deletionStatus?.has_pending_request && (
        <div className="rounded-xl p-6 bg-red-50 border border-red-200">
          <div className="flex items-center gap-2 mb-4">
            <Trash2 className="w-5 h-5 text-red-600" />
            <h3 className="text-lg font-bold text-red-700">Cancellazione Account</h3>
          </div>
          <p className="text-sm text-red-700 mb-2">
            Ai sensi dell'Art. 17 del GDPR ("Diritto all'oblio"), puoi richiedere la cancellazione del tuo account e dei tuoi dati personali.
          </p>
          <p className="text-xs text-red-600 mb-4">
            <strong>Nota:</strong> Per obblighi legali, la documentazione sanitaria sarà conservata in forma anonimizzata per 10 anni.
          </p>
          
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 bg-red-600 text-white hover:bg-red-700 transition-colors"
            data-testid="request-deletion-btn"
          >
            <Trash2 className="w-4 h-4" />
            Richiedi Cancellazione Account
          </button>
        </div>
      )}

      {/* Informazioni GDPR */}
      <div className="rounded-xl p-5 bg-blue-50 border border-blue-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">I tuoi diritti ai sensi del GDPR</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Art. 15 - Diritto di accesso ai tuoi dati</li>
              <li>Art. 16 - Diritto di rettifica</li>
              <li>Art. 17 - Diritto alla cancellazione</li>
              <li>Art. 20 - Diritto alla portabilità dei dati</li>
            </ul>
            <p className="mt-2">
              Per qualsiasi richiesta: <strong>privacy@primevision.it</strong>
            </p>
          </div>
        </div>
      </div>

      {/* Modal Anteprima Dati */}
      {showDataPreview && dataPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-bold text-[#2A9FCB]">Anteprima Dati Personali</h3>
              <button 
                onClick={() => setShowDataPreview(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-5 overflow-y-auto flex-1">
              {/* Summary */}
              <div className="mb-4 p-4 bg-[#2A9FCB]/5 rounded-xl">
                <h4 className="font-semibold text-[#2A9FCB] mb-2">Riepilogo</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Categorie dati: <strong>{dataPreview.summary?.total_categories || 0}</strong></div>
                  <div>Appuntamenti: <strong>{dataPreview.summary?.total_appointments || 0}</strong></div>
                  <div>Visite: <strong>{dataPreview.summary?.total_visits || 0}</strong></div>
                  <div>Esami: <strong>{dataPreview.summary?.total_exams || 0}</strong></div>
                </div>
              </div>

              {/* Categorie dati */}
              <div className="space-y-3">
                {Object.entries(dataPreview.data_categories || {}).map(([category, data]) => (
                  <div key={category} className="border rounded-lg p-3">
                    <h5 className="font-medium text-gray-800 capitalize mb-2">
                      {category.replace(/_/g, ' ')}
                    </h5>
                    <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto max-h-32">
                      {JSON.stringify(data, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="p-5 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowDataPreview(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200"
              >
                Chiudi
              </button>
              <button
                onClick={() => { setShowDataPreview(false); downloadMyData(); }}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-[#2A9FCB] text-white hover:bg-[#1C729E]"
              >
                <Download className="w-4 h-4 inline mr-2" />
                Scarica
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Conferma Cancellazione */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-red-700">Conferma Cancellazione</h3>
                <p className="text-sm text-gray-500">Questa azione è irreversibile</p>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Stai per richiedere la cancellazione del tuo account. I tuoi dati saranno eliminati entro 30 giorni.
                Puoi annullare la richiesta in qualsiasi momento prima della scadenza.
              </p>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Motivo (opzionale)
                </label>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Aiutaci a migliorare indicando il motivo..."
                  className="w-full p-3 rounded-lg border border-gray-200 text-sm resize-none"
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200"
                >
                  Annulla
                </button>
                <button
                  onClick={requestAccountDeletion}
                  disabled={processing}
                  className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-deletion-btn"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Conferma Cancellazione
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GDPRManager;
